import React from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import FragmentContent from './FragmentContent';
import useDeviceType from '../hooks/useDeviceType';
import { useScrollContainer } from '../context/ScrollContainerContext';
import { FRAGMENT_STATUS } from '../features/fragmentsSlice';

/**
 * Maps a CSS class detected in the fragment HTML to class-list mutations
 * applied to the fragment container.
 *
 *   add             — classes always appended to the container
 *   excludeOnMobile — base classes to remove when deviceType is 'MOBILE'
 *
 * Adding a new fragment type only requires a new entry here — no component
 * logic changes needed (Open/Closed).
 *
 * @type {Record<string, FragmentClassConfig>}
 */
const FRAGMENT_CLASS_MAP = {
	'hours-default': {
		add: ['py-4', 'ddc-font-size-small'],
		excludeOnMobile: ['px-4']
	}
};

const BASE_CLASSES = ['ws-nav-drawer__fragment-content', 'px-4'];

/**
 * Resolves the final class list for a loaded fragment by applying
 * FRAGMENT_CLASS_MAP entries matched against the HTML content.
 *
 * @param {string|null} html - sanitized fragment HTML
 * @param {string|undefined} deviceType - 'MOBILE' | 'DESKTOP'
 * @returns {string[]}
 */
const resolveFragmentClasses = (html, deviceType) => {
	const isMobile = deviceType === 'MOBILE';
	const matched = Object.entries(FRAGMENT_CLASS_MAP).filter(([detectClass]) =>
		html?.includes(detectClass)
	);

	const add = matched.flatMap(([, config]) => config.add);
	const exclude = new Set(
		isMobile
			? matched.flatMap(([, config]) => config.excludeOnMobile || [])
			: []
	);

	const base = BASE_CLASSES.filter((c) => !exclude.has(c));
	return [...base, ...add];
};

/**
 * DrawerNavFragment — thin wrapper over FragmentContent that supplies the
 * drawer's real scroll-container observer root and its deviceType-aware
 * class-list policy. Lazy-fetches a fragment URL and renders the HTML inline,
 * showing a skeleton while loading and collapsing silently on error.
 *
 * The observer root comes from the nearest ScrollContainerContext (each
 * keep-mounted drilldown body provides its own), not a single shared ref.
 *
 * @param {DrawerNavFragmentProps} props
 */
const DrawerNavFragment = ({ url, skeletonHeight = '12rem' }) => {
	const scrollContainerRef = useScrollContainer();
	const deviceType = useDeviceType();

	const resolveClassName = ({ status, html }) => {
		const isLoading =
			status === FRAGMENT_STATUS.IDLE ||
			status === FRAGMENT_STATUS.LOADING;
		const resolvedClasses =
			status === FRAGMENT_STATUS.LOADED
				? resolveFragmentClasses(html, deviceType)
				: BASE_CLASSES;
		return setClassNames([
			...resolvedClasses,
			isLoading && 'ws-nav-drawer__fragment-content--loading'
		]);
	};

	return (
		<FragmentContent
			url={url}
			root={scrollContainerRef}
			classNameResolver={resolveClassName}
			skeletonHeight={skeletonHeight}
			// The drilldown is now keep-mounted (its bodies never unmount, only
			// toggle display), so a fragment re-initializing every time its body
			// is reshown would leak DOM nodes + listeners on each reopen — the
			// exact staircase this task removes. Opt out and initialize once,
			// matching MegaMenuFragment.
			//
			// The one-time mount-time re-init (for a URL already cached by the
			// mega menu) still runs, to scope this instance's widget ids — see
			// FragmentContent/useFragmentLoader. Expected consequence: when the
			// same widget (e.g. model-selector) is embedded in BOTH the mega menu
			// and the drawer, its bundle runs at most twice over the page's life
			// → at most ONE `hystericCall(...) invoked multiple times` warning.
			// That is bounded (≤2 inits, flat) and is NOT the unbounded leak
			// returning — do not "fix" it by re-enabling reshow re-init.
			reinitOnReshow={false}
		/>
	);
};

DrawerNavFragment.propTypes = {
	url: PropTypes.string,
	skeletonHeight: PropTypes.string
};

export default DrawerNavFragment;
